import * as React from 'react';
import { SharingDialog } from '../../offers/sharing/sharing-dialog';
import type { QuickAddOfferPickerConfirmResult } from './quick-add-offer-picker';

/**
 * Extracted from quick-add-offer-picker.tsx to keep that file under the
 * repo's max-lines lint threshold: the "Dodaj współtwórców" checkbox state
 * and the deferred `SharingDialog` wiring it drives (offer-client-collaboration,
 * CU-86cbc2qv8).
 *
 * On a successful 'creating'-mode confirm with the checkbox checked,
 * `maybeDeferToSharingDialog` stashes the result and opens `SharingDialog`
 * against the new offer's id instead of forwarding the result to
 * `handleConfirmResult` right away (which typically closes the picker) —
 * `handleConfirmResult` only runs once the dialog closes.
 */
export function useQuickAddCoCreators(
  handleConfirmResult: (result: QuickAddOfferPickerConfirmResult | undefined) => void,
) {
  const [addCoCreators, setAddCoCreators] = React.useState(false);
  const [sharingDialogOfferId, setSharingDialogOfferId] = React.useState<string | null>(null);
  const pendingCreateResultRef = React.useRef<QuickAddOfferPickerConfirmResult | undefined>(
    undefined,
  );

  /** Returns true when the result was deferred to SharingDialog (caller should stop here). */
  const maybeDeferToSharingDialog = React.useCallback(
    (result: QuickAddOfferPickerConfirmResult | undefined) => {
      if (!addCoCreators || result?.status !== 'success') return false;
      pendingCreateResultRef.current = result;
      setSharingDialogOfferId(result.offerId);
      return true;
    },
    [addCoCreators],
  );

  const handleSharingDialogClose = React.useCallback(() => {
    setSharingDialogOfferId(null);
    const result = pendingCreateResultRef.current;
    pendingCreateResultRef.current = undefined;
    handleConfirmResult(result);
  }, [handleConfirmResult]);

  const sharingDialog = sharingDialogOfferId ? (
    <SharingDialog isOpen offerId={sharingDialogOfferId} onClose={handleSharingDialogClose} />
  ) : null;

  return { addCoCreators, setAddCoCreators, maybeDeferToSharingDialog, sharingDialog };
}
